import { FC, useEffect, useMemo, useState } from 'react'
import { IcoRemove } from '@/utils/icons'
import cn from './index.module.css'
import Modal from '@common/Modal'
import { useAddAlert } from '@/hooks/useAddAlert'
import Preloader from '@UI/Preloader'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import PromoCodeField from './components/PromoCodeField/PromoCodeField'
import SliderField from '@pages/Admin/CourseSettings/components/SliderField/SliderField'
import { getTariffOldPrice, getTariffPriceWithDiscount } from './utils/tariff'
import {
  courseEnrollment,
  fetchCourseAndTariffs,
  ICheckEmailOrPhoneResponse,
  IUserEnrollment,
} from './utils/api'
import {
  CheckCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Radio, Spin } from 'antd'
import { useHistory } from 'react-router-dom'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import { checkIsEmail, checkIsPhone } from './utils/validation'
import EmailOrPhoneField from './components/EmailOrPhoneField/EmailOrPhoneField'
import { useGetSettings } from '../../tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { useSelector } from 'react-redux'
import { selectIsAuth, selectProfile } from '@store/profile.reducer'
import { smsIntegrationApi } from '@/api'
import { jsonConvert } from '@/helpers/jsonConvert'
import { sortingArray } from '@/helpers/sortingArray'
import classNames from 'classnames'

interface ICourseEnrollmentModal {
  visible: boolean
  courseId: number
  tariffId: number
  onDismiss: () => void
  onSuccess: () => void
}

const CourseEnrollmentModal: FC<ICourseEnrollmentModal> = ({
  visible,
  courseId,
  tariffId,
  onSuccess = () => {},
  onDismiss = () => {},
}) => {
  return (
    <Modal
      active={visible}
      setActive={onDismiss}
      // @ts-ignore
      className={cn.modalWrapper}
    >
      <ModalBody
        courseId={courseId}
        tariffId={tariffId}
        onSuccess={onSuccess}
        onDismiss={onDismiss}
      />
    </Modal>
  )
}

const ModalBody = ({
  courseId,
  tariffId,
  onSuccess = () => {},
  onDismiss = () => {},
}: {
  courseId: number
  tariffId: number
  onSuccess: () => void
  onDismiss: () => void
}) => {
  const isAuth = useSelector(selectIsAuth)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [course, setCourse] = useState<ICourse | null>(null)
  const [tariff, setTariff] = useState<ICourseTariff | null>(null)

  const fetchSettings = async () => {
    setIsLoading(true)
    fetchCourseAndTariffs(courseId)
      .then((course) => {
        if (course) {
          const tariff =
            course?.directionTariffs?.find((item) => item.id === tariffId) ??
            null

          setCourse(course)
          setTariff(tariff)
        }
      })
      .catch((error) => {
        console.error('Ошибка при загрузке данных:', error)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  if (isLoading) {
    return (
      <div className={cn.modalContainer}>
        <div className={cn.preloader}>
          <Preloader />
        </div>
      </div>
    )
  }

  if (!tariff || !course) {
    return (
      <div className={cn.modalContainer}>
        <div className={cn.stub}>
          <div className={cn.modalContainer}>Такого курса или тарифа нет</div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn.modalContainer}>
      <div className={cn.modalHeader}>
        <span className={cn.modalHeaderTitle}>{course?.name}</span>
        <CloseOutlined
          className={cn.closeIcon}
          onClick={onDismiss}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </div>
      <div className={cn.modalSubHeader}>{tariff?.name}</div>
      <div className={cn.contentWrapper}>
        <div className={cn.content}>
          {isAuth ? (
            <CourseEnrollment
              course={course}
              tariff={tariff}
              onSuccess={onSuccess}
            />
          ) : (
            <UnauthorizedCourseEnrollment
              course={course}
              tariff={tariff}
              onSuccess={onSuccess}
            />
          )}
          {tariff?.type !== 'free' && (
            <div className={cn.bottomLabel}>
              Курс добавляется не сразу. Нужно время, на обработку банком
              платежа
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const getSliderMin = (tariff: ICourseTariff): number => {
  switch (tariff?.type) {
    case 'stream_custom_period':
      return Math.min(...tariff?.tariffPeriods?.map((item) => item.months))

    default:
      return 1
  }
}

const SelectTariffValue = ({
  tariff,
  value,
  setValue,
}: {
  tariff: ICourseTariff
  value: number
  setValue: (value: number) => void
}) => {
  const getSliderLabel = (): string => {
    switch (tariff?.type) {
      case 'hour':
        return 'Выберите количество часов'
      case 'minute':
        return 'Выберите количество минут'
      case 'month':
        return 'Выберите количество месяцев'
      case 'stream_custom_period':
        return 'Выберите количество месяцев'

      default:
        return ''
    }
  }

  const getSliderMax = (): number => {
    switch (tariff?.type) {
      case 'hour':
        return 24
      case 'minute':
        return 3600
      case 'month':
        return 24
      case 'stream_custom_period':
        return Math.max(...tariff?.tariffPeriods?.map((item) => item.months))

      default:
        return 64
    }
  }

  const getSliderMarks = () => {
    if (tariff?.type === 'stream_custom_period') {
      return (
        tariff?.tariffPeriods?.reduce<Record<number, string>>((acc, item) => {
          acc[item.months] = item.months?.toString()
          return acc
        }, {}) ?? []
      )
    }

    return []
  }

  const getPeriodsMax = () => {
    return Math.max(...tariff?.tariffPeriods?.map((item) => item.months))
  }

  const getPeriodsMin = () => {
    return Math.min(...tariff?.tariffPeriods?.map((item) => item.months))
  }

  return (
    <>
      {['stream_custom_period'].includes(tariff?.type) && (
        <SliderField
          label={getSliderLabel()}
          value={value}
          extraContent={value?.toString()}
          step={null}
          min={getPeriodsMin()}
          max={getPeriodsMax()}
          marks={getSliderMarks()}
          onChange={setValue}
        />
      )}
      {['hour', 'minute', 'month'].includes(tariff?.type) && (
        <SliderField
          label={getSliderLabel()}
          value={value}
          extraContent={value?.toString()}
          min={getSliderMin(tariff)}
          max={getSliderMax()}
          onChange={setValue}
        />
      )}
    </>
  )
}

const FormFooter = ({
  loading,
  tariff,
  promoCodeDiscount,
  price,
  oldPrice,
  onAction,
}: {
  loading: boolean
  tariff: ICourseTariff
  promoCodeDiscount: boolean
  price: number
  oldPrice: string
  onAction: () => void
}) => {
  const isAuth = useSelector(selectIsAuth)
  const user = useSelector(selectProfile)
  const { checkSettings } = useGetSettings()
  const showOldPrice = checkSettings('catalog_showOldPrice')

  return (
    <div className={cn.footer}>
      <div className={cn.priceContainer}>
        {tariff?.type === 'free' ? (
          <div className={cn.currentPrice}>Бесплатно</div>
        ) : (
          <>
            <div className={cn.promoContainer}>
              <div className={cn.currentPrice}>{price}р</div>
              {promoCodeDiscount && (
                <div className={cn.promoTitle}>с промокодом</div>
              )}
            </div>
            {showOldPrice && oldPrice && (
              <div className={cn.oldPrice}>{oldPrice}р</div>
            )}
          </>
        )}
      </div>
      {(!isAuth || user?.role === 'children') && (
        <div className={cn.footerActionWrapper}>
          {loading && (
            <Spin
              className={cn.footerActionSpinner}
              indicator={
                <LoadingOutlined
                  spin
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
            />
          )}
          <SecondaryButton
            className={cn.actionButton}
            onClick={onAction}
            disabled={loading}
          >
            {tariff?.type === 'free' ? 'Записаться' : 'Купить'}
          </SecondaryButton>
        </div>
      )}
    </div>
  )
}

const FormFooterForHasCourse = ({}) => {
  const history = useHistory()

  const onClick = () => {
    history.push('/auth')
  }

  return (
    <>
      <div className={cn.hasCourseLabel}>
        На платформе существует кабинет с таким имейлом и в нём уже есть данный
        курс
      </div>
      <div className={cn.footer}>
        <div className={cn.footerActionWrapper}>
          <SecondaryButton className={cn.actionButton} onClick={onClick}>
            Войти
          </SecondaryButton>
        </div>
      </div>
    </>
  )
}

const SuccessEnrollmentStub = () => {
  return (
    <div className={cn.successStubWrapper}>
      <div className={cn.successStubWrapperIconWrapper}>
        <CheckCircleOutlined
          className={cn.successStubWrapperIcon}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </div>
      <div className={cn.successStubWrapperColumn}>
        <div className={cn.successStubTitle}>Поздравляем с покупкой курса</div>
        <div className={cn.successStubDescription}>
          Ожидайте в течении дня с вами свяжется персональный менеджер для
          уточнения информации
        </div>
      </div>
    </div>
  )
}

const CourseEnrollment = ({
  course,
  tariff,
  onSuccess,
}: {
  course: ICourse
  tariff: ICourseTariff
  onSuccess: () => void
}) => {
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)

  const [promoCodeDiscount, setPromoCodeDiscount] =
    useState<IPromoCodeDiscount | null>(null)
  const [selectedValue, setSelectedValue] = useState<number>(
    getSliderMin(tariff),
  )

  const price = useMemo(() => {
    return getTariffPriceWithDiscount(tariff, selectedValue, promoCodeDiscount)
  }, [tariff.first_price, promoCodeDiscount, selectedValue])

  const oldPrice = useMemo(() => {
    return getTariffOldPrice(tariff, selectedValue)
  }, [tariff.old_price, selectedValue])

  const onEnroll = async () => {
    setLoading(true)
    if (course?.id && tariff.id) {
      const res = await courseEnrollment(
        course?.id,
        tariff.id,
        promoCodeDiscount?.id,
        selectedValue,
      )
      if (res && tariff?.type !== 'free') {
        window.open(res?.payment_link, '_blank')
      }
      if (res) {
        addAlert({
          title: 'Курс приобретен!',
        })
        setIsSuccess(true)
        onSuccess()
      } else {
        addAlert({
          title: 'Возникла ошибка!',
          type: 'error',
        })
      }
    }
    setLoading(false)
  }

  if (isSuccess) {
    return <SuccessEnrollmentStub />
  }

  return (
    <div className={cn.container}>
      {tariff?.type !== 'free' && (
        <>
          <SelectTariffValue
            value={selectedValue}
            tariff={tariff}
            setValue={setSelectedValue}
          />
          <PromoCodeField
            code={promoCodeDiscount}
            setCode={setPromoCodeDiscount}
          />
        </>
      )}
      <FormFooter
        tariff={tariff}
        promoCodeDiscount={!!promoCodeDiscount}
        price={price}
        oldPrice={oldPrice}
        loading={loading}
        onAction={onEnroll}
      />
    </div>
  )
}
interface ConfirmationMethod {
  key: string;
  label: string;
  order: number;
}
const UnauthorizedCourseEnrollment = ({
  course,
  tariff,
  onSuccess,
}: {
  course: ICourse
  tariff: ICourseTariff
  onSuccess: () => void
}) => {
  const addAlert = useAddAlert()
  const history = useHistory()
  const { checkSettings, getSettings, loadingSettings } = useGetSettings()
  const registrationTypeForCatalog = getSettings('registrationTypeForCatalog') ?? 'email'; //тип решистрации
  
  const requiredNumberConfirmationForCatalog = checkSettings('requiredNumberConfirmationForCatalog') //обязательно ли подтверждать телефон
  const sms_integration_enabled = checkSettings('sms_integration_enabled') //активна ли интеграция с смс
  const confirmationByPhoneNumber = checkSettings('confirmationByPhoneNumber') //есть вариант подтвердить по смс на телефон
  const confirmationByTelegram = checkSettings('confirmationByTelegram') //есть вариант подтвердить по телеграмм
  const ordersForConfirmation = !!getSettings('ordersForConfirmation')?.length ? jsonConvert(getSettings('ordersForConfirmation')) : [];
  const [code, setCode] = useState('');
  //порядок полей телеграмм и телефон
  const [codeDeliveryMethod, setCodeDeliveryMethod] = useState('') // 'sms' или 'telegram'//выбранный метод пользователем
  const [confirmationCheckboxes, setConfirmationCheckboxes] = useState<ConfirmationMethod[]>([]); //сюда поместим с правильным
  // порядком чекбоксы выбора метода пользователем
  const [token, setToken] = useState('');//токен для подтверждения
  
  useEffect(()=>{
    if(loadingSettings) return;
    const newData = sortingArray([
      ...(confirmationByPhoneNumber ? [{key:'sms', label:'Смс', order: ordersForConfirmation?.findIndex((el: string) => el=='confirmationByPhoneNumber')}] : []),
      ...(confirmationByTelegram ? [{key: 'telegram', label: 'Telegram', order: ordersForConfirmation?.findIndex((el: string) => el=='confirmationByTelegram')}] : []),
    ], 'order');

    if(!newData?.length) return;

    setCodeDeliveryMethod(newData?.[0]?.key)//по умолчанию ставим первый видимый
    setConfirmationCheckboxes(newData)
   
  }, [loadingSettings])

  const [userExistedData, setUserExistedData] = useState<ICheckEmailOrPhoneResponse>({//найденный пользователь с введенным email или телефоном
    user: null,
    hasCourse: false,
  })
  const smsIntegrationEnabled = sms_integration_enabled && 
        requiredNumberConfirmationForCatalog && 
        ( confirmationByPhoneNumber || confirmationByTelegram ) && 
        registrationTypeForCatalog === 'phone' && 
        !userExistedData.user
  //нужно ли подтверждать номер исходя их всех условий
  
  const getToken = !token && smsIntegrationEnabled;//нужно ли получать токен

  const [loading, setLoading] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [user, setUser] = useState<IUserEnrollment>({
    name: '',
    password: '',
    phone: '',
    email: '',
  })

  const [promoCodeDiscount, setPromoCodeDiscount] =
    useState<IPromoCodeDiscount | null>(null)
  const [selectedValue, setSelectedValue] = useState<number>(
    getSliderMin(tariff),
  )

  const userHasCourse = userExistedData?.user && userExistedData?.hasCourse
  const showAdditionalField = !userExistedData?.user

  const price = useMemo(() => {
    return getTariffPriceWithDiscount(tariff, selectedValue, promoCodeDiscount)
  }, [tariff.first_price, promoCodeDiscount, selectedValue])

  const oldPrice = useMemo(() => {
    return getTariffOldPrice(tariff, selectedValue)
  }, [tariff.old_price, selectedValue])

  const validateForm = (): boolean => {
    const errors: string[] = []
    if (showAdditionalField && user.name.length < 3) {
      errors.push('name')
    }
    if (!checkIsEmail(user.email) && registrationTypeForCatalog === 'email') {
      errors.push('email')
    }
    if (!checkIsPhone(user.phone) && registrationTypeForCatalog === 'phone') {
      errors.push('phone')
    }
    if (showAdditionalField && !user.password && !smsIntegrationEnabled) {
      errors.push('password')
    }
    setValidationErrors((prev) => Array.from(new Set(errors)))

    if (errors?.[0]) {
      let error = 'Ошибка на форме'
      switch (errors?.[0]) {
        case 'name':
          error = 'Заполните ФИО'
          break
        case 'email':
          error = 'Проверьте поле Email'
          break
        case 'phone':
          error = 'Проверьте поле Телефон'
          break
        case 'password':
          error = 'Проверьте пароль, он должен быть заполнен'
          break

        default:
          break
      }

      addAlert({
        title: error,
        type: 'error',
      })
      return false
    }
    return true
  }

  const onEnroll = async () => {
    setLoading(true)
    if (course?.id && tariff.id && validateForm()) {
      const res = await courseEnrollment(
        course?.id,
        tariff.id,
        promoCodeDiscount?.id,
        selectedValue,
        user,
        token,
        code,
        !!userExistedData?.user,
      )
      if (res && tariff?.type !== 'free') {
        window.open(res?.payment_link, '_blank')
      }
      if (res) {
        addAlert({
          title: 'Курс приобретен!',
        })
        setIsSuccess(true)
        onSuccess()
        history.push('/auth')
      } else {
        addAlert({
          title: 'Возникла ошибка!',
          type: 'error',
        })
      }
    }
    setLoading(false)
  }
  const getTokenFromServer = () => {
    smsIntegrationApi.getToken(
      { 
          phone: user.phone,
          type: [codeDeliveryMethod],
      }
    )
    .then((res)=>{
        if(res?.data?.status === "success"){
            setToken(res?.data?.token)
            addAlert({title: 'Код отправлен', text: 'Заполните поле с кодом подтверждения'})
        }
        else{
            // @ts-ignore
            addAlert({title: 'Ошибка', text: res?.response?.data?.error, type: 'error'})
        }
    })
  }

  const updateUser = (field: keyof IUserEnrollment, value: string) => {
    setUser((prev) => {
      return { ...prev, [field]: value }
    })
  }

  const updateEmail = (value: string) => {
    updateUser('email', value)
    if (checkIsEmail(value)) {
      setValidationErrors((prev) => [
        ...prev?.filter((item) => item !== 'email'),
      ])
    } else {
      setValidationErrors((prev) => Array.from(new Set([...prev, 'email'])))
    }
  }

  const updatePhone = (value: string) => {
    updateUser('phone', value)
    if (checkIsPhone(value)) {
      setValidationErrors((prev) => [
        ...prev?.filter((item) => item !== 'phone'),
      ])
    } else {
      setValidationErrors((prev) => Array.from(new Set([...prev, 'phone'])))
    }
  }

  if(loadingSettings){
    return <Preloader />
  }
  
  if (isSuccess) {
    return <SuccessEnrollmentStub />
  }

  return (
    <div className={cn.container}>
      <EmailOrPhoneField
          courseId={Number(course.id)}
          value={registrationTypeForCatalog === 'phone' ? user.phone : user.email}
          type={registrationTypeForCatalog === 'email' ? 'email' : 'phone'}
          onChange={registrationTypeForCatalog === 'phone' ? updatePhone : updateEmail}
          isInvalid={registrationTypeForCatalog === 'phone' ? validationErrors?.includes('phone') : validationErrors?.includes('email')}
          onExistedChange={setUserExistedData}
          existedData={userExistedData}
          byConfirmationCode={smsIntegrationEnabled}
        />
        {
          !!token && <TextField
            className={classNames(cn.inputField, cn.inputFieldKode)}
            placeholder="Код"
            value={code}
            onChange={setCode}
          />
        }

      {
        getToken && (
          <div className={cn.radioGroupWrapper}>
            <span className={classNames(cn.text, cn.textRed)}>
              {confirmationCheckboxes?.length > 1 && 'Выберите, куда отправить вам временный код для подтверждения'}
              {confirmationCheckboxes?.length == 1 && (confirmationByPhoneNumber) && 'Временный код для подтверждения будет отправлен по смс'}
              {confirmationCheckboxes?.length == 1 && (confirmationByTelegram) && 'Временный код для подтверждения будет отправлен в телеграм'}
            </span>
            {
              confirmationCheckboxes?.length > 1 && (
                <Radio.Group 
                    value={codeDeliveryMethod}
                    onChange={(e) => setCodeDeliveryMethod(e.target.value)}
                  > 
                    {
                      confirmationCheckboxes.map((el)=>{
                        return(
                          <Radio value={el.key}>{el.label}</Radio>
                        )
                      })
                    }
                  </Radio.Group>  
              )
            }
          </div>
        )
      }

      {showAdditionalField && (
        <TextField
          className={cn.inputField}
          placeholder="ФИО"
          value={user.name}
          onChange={(value) => updateUser('name', value)}
          isInvalid={validationErrors?.includes('name')}
        />
      )}
      {showAdditionalField && !smsIntegrationEnabled && (
        <TextField
          className={cn.inputField}
          placeholder="Придумайте пароль"
          value={user.password}
          onChange={(value) => updateUser('password', value)}
          isInvalid={validationErrors?.includes('password')}
        />
      )}
      {!userHasCourse && tariff?.type !== 'free' && (
        <>
          <SelectTariffValue
            value={selectedValue}
            tariff={tariff}
            setValue={setSelectedValue}
          />
          <PromoCodeField
            code={promoCodeDiscount}
            setCode={setPromoCodeDiscount}
          />
        </>
      )}
      {userHasCourse ? (
        <FormFooterForHasCourse />
      ) : (
        <FormFooter
          tariff={tariff}
          promoCodeDiscount={!!promoCodeDiscount}
          price={price}
          oldPrice={oldPrice}
          loading={loading}
          onAction={getToken ? getTokenFromServer : onEnroll}
        />
      )}
    </div>
  )
}

export default CourseEnrollmentModal
